// FilterParser.ts - Updated with proper current state vs expected next logic

import { FilterTokenizer } from './FilterTokenizer';
import { Token } from './types';
import {
  FilterAST,
  Expression,
  FieldNode,
  OperatorNode,
  ValueNode,
  LogicalOperator,
  IncompleteNode,
  ParseResult,
  ParseContext,
  ParseError,
  Position,
  BaseNode,
  ASTUtils,
} from './FilterAST';
import {
  FilterSemanticValidator,
  SemanticValidationResult,
} from './FilterSemanticValidator';
import { FilterCriteria } from '../../constants';

export class FilterParser {
  private tokens: Token[] = [];
  private position = 0;
  private errors: ParseError[] = [];
  private incompleteNodes: IncompleteNode[] = [];
  private cursorPosition?: number;
  private input = '';

  constructor(private filterOptions: Record<string, any>) {}

  parse(input: string, cursorPosition?: number): ParseResult {
    this.input = input;
    this.tokens = FilterTokenizer.tokenize(input).tokens;
    this.position = 0;
    this.errors = [];
    this.incompleteNodes = [];
    this.cursorPosition = cursorPosition;

    const ast = this.parseFilter();
    const context =
      cursorPosition !== undefined
        ? this.getContextAtPosition(ast, cursorPosition, input)
        : null;

    // Perform semantic validation only if syntax is valid
    let semanticValidation: SemanticValidationResult | undefined;
    const isSyntaxValid =
      this.errors.length === 0 &&
      ASTUtils.isFilterComplete(ast, (name) => this.isBooleanField(name));

    if (isSyntaxValid && this.filterOptions) {
      const semanticValidator = new FilterSemanticValidator(
        this.filterOptions as Record<string, FilterCriteria>
      );
      semanticValidation = semanticValidator.validate(ast);
    }

    return {
      ast,
      isValid: isSyntaxValid && semanticValidation?.isValid !== false,
      errors: this.errors,
      context,
      incompleteNodes: this.incompleteNodes,
      semanticValidation,
    };
  }
  private parseFilter(): FilterAST {
    const expressions: Expression[] = [];
    const logicalOperators: LogicalOperator[] = [];
    const start = this.getCurrentPosition();

    // Parse first expression
    if (!this.isAtEnd()) {
      const expr = this.parseExpression();
      if (expr) {
        expressions.push(expr);
      }
    }

    // Parse additional expressions with logical operators
    while (!this.isAtEnd() && this.isLogicalOperator()) {
      const logOp = this.parseLogicalOperator();
      if (logOp) {
        logicalOperators.push(logOp);
      }

      if (!this.isAtEnd()) {
        const expr = this.parseExpression();
        if (expr) {
          expressions.push(expr);
        }
      } else {
        this.addIncompleteNode('Field', this.getCurrentPosition());
        break;
      }
    }

    const end = this.getCurrentPosition();

    return {
      type: 'Filter',
      position: { start, end },
      expressions,
      logicalOperators,
    };
  }

  private parseExpression(): Expression | null {
    const start = this.getCurrentPosition();

    if (this.match('(')) {
      return this.parseGroupExpression(start);
    }

    const field = this.parseField();
    if (!field) {
      return null;
    }

    let operator: OperatorNode | null = null;
    let value: ValueNode | null = null;

    const isBooleanField = this.isBooleanField(field.name);

    if (!isBooleanField) {
      if (this.isOperator()) {
        operator = this.parseOperator();

        if (operator) {
          if (this.isValue()) {
            value = this.parseValue();
          } else {
            this.addIncompleteNode('Value', this.getCurrentPosition());
          }
        }
      } else if (
        !this.isLogicalOperator() &&
        !this.isAtEnd() &&
        !this.match(')')
      ) {
        this.addIncompleteNode('Operator', this.getCurrentPosition());
      }
    }

    const end = this.getCurrentPosition();

    return {
      type: 'Expression',
      position: { start, end },
      field,
      operator: operator || undefined,
      value: value || undefined,
    };
  }

  private parseGroupExpression(start: number): Expression {
    const content = this.parseFilter();

    if (!this.match(')')) {
      this.addError('Expected closing parenthesis', this.getCurrentPosition());
    }

    const end = this.getCurrentPosition();

    return {
      type: 'GroupExpression',
      position: { start, end },
      content,
    };
  }

  private parseField(): FieldNode | null {
    const token = this.getCurrentToken();
    if (!token || token.type !== 'field') {
      if (!this.isAtEnd()) {
        this.addError(
          `Expected field name, got ${token?.type || 'end of input'}`,
          this.getCurrentPosition()
        );
      }
      return null;
    }

    this.advance();

    return {
      type: 'Field',
      position: { start: token.start, end: token.end },
      name: token.value,
      raw: token.raw,
    };
  }

  private parseOperator(): OperatorNode | null {
    const token = this.getCurrentToken();
    if (!token || token.type !== 'operator') {
      this.addError(
        `Expected operator, got ${token?.type || 'end of input'}`,
        this.getCurrentPosition()
      );
      return null;
    }

    this.advance();

    return {
      type: 'Operator',
      position: { start: token.start, end: token.end },
      operator: token.value,
      raw: token.raw,
    };
  }

  private parseValue(): ValueNode | null {
    const token = this.getCurrentToken();
    if (!token || token.type !== 'value') {
      this.addError(
        `Expected value, got ${token?.type || 'end of input'}`,
        this.getCurrentPosition()
      );
      return null;
    }

    this.advance();

    return {
      type: 'Value',
      position: { start: token.start, end: token.end },
      value: token.value,
      raw: token.raw,
      isQuoted: token.raw.startsWith('"') || token.raw.startsWith("'"),
    };
  }

  private parseLogicalOperator(): LogicalOperator | null {
    const token = this.getCurrentToken();
    if (!token || token.type !== 'logical') {
      this.addError(
        `Expected logical operator, got ${token?.type || 'end of input'}`,
        this.getCurrentPosition()
      );
      return null;
    }

    this.advance();

    return {
      type: 'LogicalOperator',
      position: { start: token.start, end: token.end },
      operator: token.value as 'AND' | 'OR',
      raw: token.raw,
    };
  }

  private getContextAtPosition(
    ast: FilterAST,
    position: number,
    input: string
  ): ParseContext {
    // Use recursive search to handle nested expressions
    const searchResult = this.searchContextRecursively(ast, position, 0);

    let { nodeAtPosition, expressionAtPosition, nestedAst } = searchResult;

    // SPECIAL CASE: If we have a GroupExpression as parent but need the nested field context
    // This handles when cursor is inside parentheses but we need field info from nested expressions
    if (
      expressionAtPosition?.type === 'GroupExpression' &&
      nestedAst &&
      nestedAst.expressions.length > 0
    ) {
      const nestedGrammarState = this.analyzeGrammarState(nestedAst);

      if (
        nestedGrammarState.expecting === 'Operator' ||
        nestedGrammarState.expecting === 'Value'
      ) {
        const lastNestedExpression =
          nestedAst.expressions[nestedAst.expressions.length - 1];
        if (lastNestedExpression && lastNestedExpression.field) {
          // Replace the GroupExpression with the actual expression containing the field
          expressionAtPosition = lastNestedExpression;
        }
      }
    }

    // FALLBACK STRATEGY: If no expression found but we have expressions, use grammar analysis
    // Apply fallback to the most relevant AST (nested or top-level)
    const relevantAst = nestedAst || ast;
    if (!expressionAtPosition && relevantAst.expressions.length > 0) {
      const grammarState = this.analyzeGrammarState(relevantAst);

      // If grammar expects an operator or value, the last expression is our target
      if (
        grammarState.expecting === 'Operator' ||
        grammarState.expecting === 'Value'
      ) {
        const lastExpression =
          relevantAst.expressions[relevantAst.expressions.length - 1];
        if (lastExpression && lastExpression.field) {
          expressionAtPosition = lastExpression;
        }
      }
    }

    // SPECIAL CASE: If we have a nested AST but no specific expression,
    // and the nested AST has expressions, use the relevant one from the nested AST
    if (
      !expressionAtPosition &&
      nestedAst &&
      nestedAst.expressions.length > 0
    ) {
      const nestedGrammarState = this.analyzeGrammarState(nestedAst);

      if (
        nestedGrammarState.expecting === 'Operator' ||
        nestedGrammarState.expecting === 'Value'
      ) {
        const lastNestedExpression =
          nestedAst.expressions[nestedAst.expressions.length - 1];
        if (lastNestedExpression && lastNestedExpression.field) {
          expressionAtPosition = lastNestedExpression;
        }
      }
    }

    // Better partial text detection
    const beforeCursor = input.substring(0, position);
    const partialWordMatch = beforeCursor.match(/(\w+)$/);
    const partialText = partialWordMatch ? partialWordMatch[1] : '';

    let currentState: ParseContext['currentState'];
    let expectsNext: ParseContext['expectsNext'];
    let nodeType: ParseContext['nodeType'] = 'Unknown';

    // CASE 1: We found a token
    if (nodeAtPosition) {
      switch (nodeAtPosition.type) {
        case 'Field':
          currentState = 'EditingField';
          expectsNext = this.isBooleanField((nodeAtPosition as any).name)
            ? 'LogicalOperator'
            : 'Operator';
          nodeType = 'Field';
          break;
        case 'Operator':
          currentState = 'EditingOperator';
          expectsNext = 'Value';
          nodeType = 'Operator';
          break;
        case 'Value':
          currentState = 'EditingValue';
          expectsNext = 'LogicalOperator';
          nodeType = 'Value';
          break;
        case 'LogicalOperator':
          currentState = 'EditingLogicalOperator';
          expectsNext = 'Field';
          nodeType = 'LogicalOperator';
          break;
        default:
          currentState = 'BetweenTokens';
          expectsNext = 'Field';
      }
    }
    // CASE 2: We're typing a partial word
    else if (partialText.length > 0) {
      const grammarState = this.analyzeGrammarState(relevantAst);

      switch (grammarState.expecting) {
        case 'Field':
          currentState = 'TypingField';
          expectsNext = 'Operator';
          nodeType = 'Field';
          break;
        case 'Operator':
          currentState = 'TypingOperator';
          expectsNext = 'Value';
          nodeType = 'Operator';
          break;
        case 'Value':
          currentState = 'TypingValue';
          expectsNext = 'LogicalOperator';
          nodeType = 'Value';
          break;
        case 'LogicalOperator':
          currentState = 'TypingLogicalOperator';
          expectsNext = 'Field';
          nodeType = 'LogicalOperator';
          break;
        default:
          currentState = 'TypingField';
          expectsNext = 'Operator';
          nodeType = 'Field';
      }
    }
    // CASE 3: Between tokens
    else {
      currentState = 'BetweenTokens';

      const grammarState = this.analyzeGrammarState(relevantAst);
      expectsNext = grammarState.expecting;
      nodeType = 'Expression';
    }

    const result: ParseContext = {
      currentState,
      expectsNext,
      nodeType,
      parentExpression: expressionAtPosition || undefined,
      currentNode: nodeAtPosition || undefined,
      isInParentheses: this.isPositionInParentheses(ast, position),
      isIncomplete: false,
      partialText: partialText || undefined,
    };

    return result;
  }

  private searchContextRecursively(
    ast: FilterAST,
    position: number,
    depth: number
  ): {
    nodeAtPosition: BaseNode | null;
    expressionAtPosition: Expression | null;
    nestedAst: FilterAST | null;
  } {
    const indent = '  '.repeat(depth);

    let nodeAtPosition: BaseNode | null = null;
    let expressionAtPosition: Expression | null = null;
    let nestedAst: FilterAST | null = null;

    for (let i = 0; i < ast.expressions.length; i++) {
      const expr = ast.expressions[i];

      // Check if position is within this expression's bounds
      // For GroupExpressions, be more inclusive with the boundary check
      // to handle cases where cursor is just outside the closing parenthesis
      const isWithinBounds =
        expr.type === 'GroupExpression'
          ? position >= expr.position.start && position <= expr.position.end + 1
          : position >= expr.position.start && position <= expr.position.end;

      if (isWithinBounds) {
        // If this is a GroupExpression, search recursively within its content
        if (expr.type === 'GroupExpression' && expr.content) {
          const nestedResult = this.searchContextRecursively(
            expr.content,
            position,
            depth + 1
          );

          // If we found something in the nested content, use that
          if (
            nestedResult.nodeAtPosition ||
            nestedResult.expressionAtPosition
          ) {
            return {
              nodeAtPosition: nestedResult.nodeAtPosition,
              expressionAtPosition: nestedResult.expressionAtPosition,
              nestedAst: expr.content, // Return the nested AST for grammar analysis
            };
          }

          // If nothing found in nested content but position is within group bounds,
          // this group expression is still our parent
          expressionAtPosition = expr;
          nestedAst = expr.content;
        } else {
          // Regular expression - set as parent
          expressionAtPosition = expr;
        }
      }

      // Search within individual nodes of this expression
      // Check field
      if (expr.field) {
        const field = expr.field;
        const inField =
          position >= field.position.start && position <= field.position.end;

        if (inField) nodeAtPosition = field;
      }

      // Check operator
      if (expr.operator) {
        const op = expr.operator;
        const inOp =
          position >= op.position.start && position <= op.position.end;

        if (inOp) nodeAtPosition = op;
      }

      // Check value
      if (expr.value) {
        const val = expr.value;
        const inVal =
          position >= val.position.start && position <= val.position.end;

        if (inVal) {
          nodeAtPosition = val;
        }
      }

      // If we found a specific node, we can return early
      if (nodeAtPosition) {
        return { nodeAtPosition, expressionAtPosition, nestedAst };
      }
    }

    // Also check logical operators at this AST level
    for (let i = 0; i < ast.logicalOperators.length; i++) {
      const logOp = ast.logicalOperators[i];
      const inLogOp =
        position >= logOp.position.start && position <= logOp.position.end;

      if (inLogOp) {
        nodeAtPosition = logOp;
        return { nodeAtPosition, expressionAtPosition, nestedAst };
      }
    }

    return { nodeAtPosition, expressionAtPosition, nestedAst };
  }
  private analyzeGrammarState(ast: FilterAST): {
    expecting: ParseContext['expectsNext'];
  } {
    // No expressions yet - need to start with a field
    if (ast.expressions.length === 0) {
      return { expecting: 'Field' };
    }

    const lastExpression = ast.expressions[ast.expressions.length - 1];
    const expectedLogicalOps = ast.expressions.length - 1;
    const actualLogicalOps = ast.logicalOperators.length;

    // Check if last expression is incomplete
    if (!lastExpression.field) {
      return { expecting: 'Field' };
    }

    const isBoolean = this.isBooleanField(lastExpression.field.name);

    // For boolean fields, field is enough - check if we need logical operator
    if (isBoolean) {
      if (actualLogicalOps > expectedLogicalOps) {
        return { expecting: 'Field' };
      } else if (actualLogicalOps < expectedLogicalOps) {
        return { expecting: 'LogicalOperator' };
      } else {
        return { expecting: 'LogicalOperator' };
      }
    }

    // For non-boolean fields, need operator and value
    if (!lastExpression.operator) {
      return { expecting: 'Operator' };
    }

    if (!lastExpression.value) {
      return { expecting: 'Value' };
    }

    // Expression is complete - check logical operators
    if (actualLogicalOps < expectedLogicalOps) {
      return { expecting: 'LogicalOperator' };
    } else if (actualLogicalOps > expectedLogicalOps) {
      // We have a trailing logical operator, expecting new field
      return { expecting: 'Field' };
    } else {
      // Complete state - can add logical operator
      return { expecting: 'LogicalOperator' };
    }
  }
  private isPositionInParentheses(ast: FilterAST, position: number): boolean {
    return !!ASTUtils.findNodeAtPosition(ast, position)?.type.includes('Group');
  }

  // Helper methods
  private getCurrentToken(): Token | null {
    return this.position < this.tokens.length
      ? this.tokens[this.position]
      : null;
  }

  private advance(): Token | null {
    const token = this.getCurrentToken();
    if (!this.isAtEnd()) {
      this.position++;
    }
    return token;
  }

  private isAtEnd(): boolean {
    return this.position >= this.tokens.length;
  }

  private match(value: string): boolean {
    const token = this.getCurrentToken();
    if (token && (token.value === value || token.raw === value)) {
      this.advance();
      return true;
    }
    return false;
  }

  private isLogicalOperator(): boolean {
    const token = this.getCurrentToken();
    return token?.type === 'logical';
  }

  private isOperator(): boolean {
    const token = this.getCurrentToken();
    return token?.type === 'operator';
  }

  private isValue(): boolean {
    const token = this.getCurrentToken();
    return token?.type === 'value';
  }

  private getCurrentPosition(): number {
    const token = this.getCurrentToken();
    return token ? token.start : this.tokens[this.position - 1]?.end || 0;
  }

  private isBooleanField(fieldName: string): boolean {
    const upperField = fieldName.toUpperCase();
    const fieldConfig = this.filterOptions[upperField];
    return fieldConfig?.data_types?.includes('boolean') || false;
  }

  private addError(message: string, position: number): void {
    this.errors.push({
      message,
      position: { start: position, end: position },
      severity: 'error',
    });
  }

  private addIncompleteNode(
    expectedType: IncompleteNode['expectedType'],
    position: number
  ): void {
    this.incompleteNodes.push({
      type: 'Incomplete',
      position: { start: position, end: position },
      expectedType,
    });
  }
}
